import { set } from 'date-fns'
import find from 'lodash/find'
import first from 'lodash/first'
import React, {
  MutableRefObject,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  VFC,
} from 'react'

import { BuyButton } from '@Components/BuyButton'
import { MakeItPersonalButton } from '@Components/Personalization/Button/MakeItPersonalButton'
import { SelectorModal } from '@Components/Personalization/Modals/Selector'
import { PersonalisationModalTypes } from '@Components/Personalization/Modals/Shared'
import { getPersonalisationModal } from '@Components/Personalization/Util/helpers'
import { ProductBundle } from '@Components/Product/Bundle'
import { ProductContext } from '@Components/Product/Context'
import { ProductDescription } from '@Components/Product/Description'
import { ProductDimensions } from '@Components/Product/Dimensions'
import {
  ShippingModalTrigger,
  StyledCrossSells,
  StyledSizeMissing,
  StyledSizeSelector,
  StyledSizeSoldOut,
  StyledUrgencyTimer,
  VariantAndBuyButtonWrapper,
} from '@Components/Product/Display/styledElements'
import { YOLOShipping } from '@Components/Product/Display/YOLOShipping'
import { LinkedProductList } from '@Components/Product/Linked/LinkedProductList'
import { ProductMaterials } from '@Components/Product/Materials'
import { ProductShippingAndReturn } from '@Components/Product/ProductShippingAndReturn'
import { ProductReviews } from '@Components/Product/Reviews'
import { ProductSizeGuide } from '@Components/Product/SizeGuide'
import { SizeTable } from '@Components/Product/SizeGuide/SizeTable'
import { TitleAndPrice } from '@Components/Product/TitleAndPrice'
import { ProductUSPs } from '@Components/Product/USPs'
import { ShippingInfoModal } from '@Components/Shipping/ShippingInfoModal'
import { SizeSelectorState } from '@Components/SizeSelector'
import { IWithMoney, Product, ProductWithQuantity } from '@Generated/graphql'
import {
  buildLinkedProductList,
  getPersonalizationsFromProduct,
  getPersonalizationTypes,
} from '@Globals/CMS/ProductHelpers'
import { MetadataContext } from '@Globals/Metadata'
import { useMParticle } from '@Globals/MParticle/useMParticle'
import { formatPrice } from '@Globals/PriceHelpers'
import { useFilteredVariants } from '@Globals/Product/useFilteredVariants'
import { Experiment } from '@Globals/SplitTests/Experiment'
import { useTranslation } from '@Globals/Translations'

interface InitModalStateProps {
  currentModal: null | PersonalisationModalTypes
  hide: boolean
  isOpen: boolean
  selectedVariantID: string
}

const initModalState: InitModalStateProps = {
  isOpen: false,
  hide: false,
  currentModal: null,
  selectedVariantID: '',
}

export const TWO_FOR_ONE_DISCOUNT_PERCENTAGE = 30

export const Info: VFC<{ reviewWrapperRef?: MutableRefObject<HTMLDivElement | null> }> = ({ reviewWrapperRef = null }) => {
  const product = useContext(ProductContext)
  const { store } = useContext(MetadataContext)
  const { t } = useTranslation()
  const storeLocaleCode = store?.config?.localeCode || ''
  const [modalState, setModalState] = useState<InitModalStateProps>(initModalState)
  const { price } = product?.masterVariant || {}

  // Some calculations needed for the "buy two for almost-one" offer
  const shouldIncludeTwoForOneDiscount = product?.type === 'earrings'
  const buyTwoExtraPrice = Math.round((price?.value || 0) * ((100 - TWO_FOR_ONE_DISCOUNT_PERCENTAGE) / 10000))
  const buyTwoExtraPriceFormatted = shouldIncludeTwoForOneDiscount ? formatPrice(store?.id, storeLocaleCode, buyTwoExtraPrice) : ''
  const { availableVariants, variantsInTotal } = useFilteredVariants({ product })
  const atLeastPartiallyInStock = product?.availability?.isInStock
  const [variantStatus, setVariantStatus] = useState(SizeSelectorState.IDLE)

  // Preselect the first variant if there's only one anyway. If there are more, even if they're filtered out due to not
  // being restocked, we're dealing with a product that's supposed to come in many sizes, so the user will want to know
  // which size they're putting in the basket.
  const [selectedVariantID, setSelectedVariantID] = useState<string>(
    variantsInTotal > 0 ? '' : first(availableVariants)?.id || '',
  )
  const [selectedCrossSellIDs, setSelectedCrossSellIDs] = useState<Product['id'][]>([])
  const [shippingInfoModalOpen, setShippingInfoModalOpen] = useState(false)
  const [buyTwoSelectionEnabled, setBuyTwoSelectionEnabled] = useState(false)
  const [buyPersonalizationId, setBuyPersonalizationId] = useState<string | null>(null)
  const personalisationModal = useRef<ReactElement | null>(null)

  const linkedProducts = useMemo(() => buildLinkedProductList(product), [product])
  const { otherEvent } = useMParticle()

  useEffect(() => {
    if (!shippingInfoModalOpen) {
      return
    }

    otherEvent({
      attributes: {
        source_id: 'side_pane_button',
      },
      eventName: 'delivery_details_overlay_open',
    })
  }, [otherEvent, shippingInfoModalOpen])

  // We need a date object for the deadline thingie, and spawning a native date element is cheap and easy.
  const now = new Date((new Date()).toLocaleString('en-US', { timeZone: 'Europe/Copenhagen' }))
  // However, making changes to a date element is more expensive, and if we don't memo it, the object format will
  // trigger a re-render forever and ever into infinity. So we memo it. Easy.
  const deadline = useMemo(
    () => set(
      now,
      { hours: 16, minutes: 0, seconds: 0 },
    ),
    // If we just set the dependency to "now", it'd re-render every chance it got. However, locking it to the actual
    // date accomplishes everything we need, AND MORE! Well okay, not more, just that we don't re-render unless we
    // have to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [now.getDate()],
  )

  // TODO: This has to work better.
  if (!product) {
    return <>404</>
  }

  const productCanBeBought = variantStatus === SizeSelectorState.IDLE
    || variantStatus === SizeSelectorState.SIZE_AVAILABLE
  // @ts-ignore - "product.masterVariant.bundleContent" doesn't exist in the auto-generated types, but it's in the query.
  const bundleProducts: ProductWithQuantity[] = product.masterVariant?.bundleContent?.products || []
  // @ts-ignore - "product.masterVariant.bundlePrice" doesn't exist in the auto-generated types, but it's in the query.
  const bundlePriceDifference: IWithMoney = product.masterVariant?.bundlePrice?.difference

  const personalisations = getPersonalizationsFromProduct(product)
  if (personalisations) {
    const personalisationTypes = getPersonalizationTypes(personalisations)

    const options = {
      product,
      isOpen: modalState.isOpen,
      onHide: () => setModalState(prev => ({ ...prev, isOpen: false })),
      setState: setModalState,
      currentModal: modalState.currentModal,
      selectedVariantID,
      additionalIDs: selectedCrossSellIDs,
    }
    if (personalisationTypes.length > 1) {
      personalisationModal.current = SelectorModal(options)
    } else if (personalisationTypes.length === 1) {
      personalisationModal.current = (getPersonalisationModal(first(personalisationTypes) as PersonalisationModalTypes, options))
    }
  }
  const showPersonalisation = personalisations && personalisations.length > 0 && productCanBeBought
  const getPersonalizationModel: () => PersonalisationModalTypes | null = () => {
    if (!buyPersonalizationId) {
      return null
    }
    const selectedPersonalisation = find(personalisations, { id: buyPersonalizationId })
    return selectedPersonalisation?.masterVariant.type.value.key ?? null
  }
  return (
    <>
      <TitleAndPrice
        buyTwoEnabled={buyTwoSelectionEnabled}
        name={product.name}
        // @ts-ignore - we know what we're doing, son.
        price={buyTwoSelectionEnabled
          ? {
            ...price,
            format: formatPrice(store?.id, storeLocaleCode, ((price?.value || 0) / 100 + buyTwoExtraPrice)),
          }
          : price || undefined}
      />

      <ProductBundle bundleItems={bundleProducts} priceDifference={bundlePriceDifference} />

      {atLeastPartiallyInStock && (
        <>
          <YOLOShipping />

          <StyledUrgencyTimer
            deadlineUTC={deadline}
            productHasPersonalization={showPersonalisation}
          />

          <ShippingModalTrigger
            onClick={() => setShippingInfoModalOpen(true)}
            type="button"
          >
            {t('products:PP_SEE_SHIPPING_AND_RETURN_OPTIONS')}
          </ShippingModalTrigger>

          <ShippingInfoModal
            isOpen={shippingInfoModalOpen}
            onHide={() => setShippingInfoModalOpen(false)}
            sourceID="pdp_below_urgency_timer"
          />
          <VariantAndBuyButtonWrapper>
            {variantsInTotal > 1 && (
              <StyledSizeSelector
                lastChild={!productCanBeBought}
                onChange={({ target }) => setSelectedVariantID(target.value)}
                onStateChange={setVariantStatus}
                sizes={availableVariants}
                value={selectedVariantID}
                withoutLabelAnimation
              />
            )}
            {productCanBeBought && (
              <BuyButton
                additionalIDs={selectedCrossSellIDs}
                buttonText={t('cms:ADD_TO_CART')}
                className="buy-button"
                handleOnClick={buyPersonalizationId
                  ? () => {
                    setModalState({
                      ...modalState,
                      isOpen: true,
                      currentModal: (getPersonalizationModel() ?? modalState.currentModal),
                      selectedVariantID,
                    })
                    if (personalisationModal?.current?.props) {
                      personalisationModal.current = { ...personalisationModal.current, props: { ...personalisationModal.current.props, isOpen: true } }
                    }
                  }
                  : undefined}
                isSaleable
                product={product}
                quantity={buyTwoSelectionEnabled ? 2 : 1}
                showPrice={false}
                unwrapped
                variantID={selectedVariantID}
              />
            )}
            {(buyPersonalizationId && modalState.isOpen) && personalisationModal.current}
          </VariantAndBuyButtonWrapper>

          {product?.sizeGuide && (
            <ProductSizeGuide
              blocks={product.sizeGuide.blocks || []}
              id="side-pane"
              title={product.sizeGuide.title || ''}
              variant="accordion-side-pane"
            >
              <SizeTable product={product} />
            </ProductSizeGuide>
          )}
        </>
      )}
      {showPersonalisation && (
        <Experiment
          A={(
            <>
              <MakeItPersonalButton
                callback={() => {
                  setModalState({
                    ...modalState,
                    isOpen: true,
                    selectedVariantID,
                  })
                }}
              />
              {personalisationModal.current}
            </>
          )}
          test="new_pp_cross_design_v1"
        />

      )}

      {variantStatus === SizeSelectorState.MY_SIZE_DOESNT_EXIST && (
        <StyledSizeMissing product={product} />
      )}

      {atLeastPartiallyInStock && variantStatus === SizeSelectorState.SIZE_SOLD_OUT && (
        <StyledSizeSoldOut product={product} selectedSize={selectedVariantID} />
      )}

      {!atLeastPartiallyInStock && <StyledSizeSoldOut product={product} showSizeSelector />}

      {atLeastPartiallyInStock
        && (
          <StyledCrossSells
            buyPersonalizationId={buyPersonalizationId}
            buyTwoAllowed={shouldIncludeTwoForOneDiscount}
            buyTwoPrice={buyTwoExtraPriceFormatted}
            buyTwoSelected={buyTwoSelectionEnabled}
            onBuyPersonalizationId={setBuyPersonalizationId}
            onBuyTwoChanged={setBuyTwoSelectionEnabled}
            product={product}
            selectedIDs={selectedCrossSellIDs}
            setSelectedIDs={setSelectedCrossSellIDs}
          />
        )}

      <ProductUSPs
        buyTwoAllowed={shouldIncludeTwoForOneDiscount}
        buyTwoEnabled={buyTwoSelectionEnabled}
        product={product}
      />

      <ProductDescription product={product} selectedVariantID={selectedVariantID} />

      <ProductDimensions asSection product={product} selectedVariantID={selectedVariantID} />

      <ProductMaterials asSection product={product} selectedVariantID={selectedVariantID} />

      {product.productReviews && product.productReviews.count > 0
        && (
          <div ref={reviewWrapperRef}>
            <ProductReviews reviews={product.productReviews} />
          </div>
        )}

      <ProductShippingAndReturn />

      <LinkedProductList data={linkedProducts} selectedID={product.id} />
    </>
  )
}
